import styled, { css, keyframes } from 'styled-components'

const MoveHeader = keyframes`
from {
	top : -10%;
	box-shadow : none;
}
to {
	top : 0%;
	box-shadow : 0px 2px 10px -1px #00000024;
}
`
export const MainHeaderContainer = styled.div<{ fixed: boolean; layoutConfigFixed: boolean }>`
	position: fixed;
	box-shadow: ${props => (!props.fixed ? 'none' : '')};
	z-index: 99;
	background: var(--surface-card);
	border-bottom: 1px solid var(--border);

	animation: ${props =>
		props.fixed
			? css`
					${MoveHeader} 500ms ease-in-out forwards
			  `
			: ''};
	/* 100% (not 100vw) so the fixed bar excludes the scrollbar — avoids RTL/
	   horizontal-overflow on mobile. */
	width: 100%;
`

/* ===== Mobile TopBar (<1024px) — 56px, logo-mode or back+title-mode ===== */
export const MobileTopBar = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	gap: var(--space-1);
	height: var(--topbar-height, 56px);
	padding-inline: var(--space-3) var(--space-2);
	@media (min-width: 1024px) {
		display: none;
	}
`
export const TopBarIconButton = styled.button`
	flex: none;
	width: 40px;
	height: 40px;
	border: none;
	border-radius: var(--r-md);
	background: transparent;
	color: var(--text-primary);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: var(--font-sans);
	transition: background var(--dur-fast) var(--ease);
	&:hover {
		background: var(--surface-sunken-strong);
	}
	svg {
		width: 20px;
		height: 20px;
	}
`
export const TopBarActions = styled.div`
	display: flex;
	align-items: center;
	gap: var(--space-0_5);
	margin-inline-start: auto;
`

/* ===== Back strip (all breakpoints) — slim row under the bar on drill-down
   routes. The logo TopBar / desktop bar above it never change. ===== */
export const SubHeaderStrip = styled.div`
	display: flex;
	align-items: center;
	gap: var(--space-3);
	height: 36px;
	padding: 0 var(--space-3);
	border-top: 1px solid var(--border);
	background: var(--surface-card);
	.title {
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font: var(--fw-medium) 13px var(--font-sans);
		color: var(--text-secondary);
	}
	@media (min-width: 1024px) {
		height: 40px;
		padding: 0 var(--space-6);
	}
`
export const BackChip = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 28px;
	padding: 0 10px;
	border: none;
	border-radius: var(--r-md);
	background: transparent;
	color: var(--text-secondary);
	font: var(--fw-medium) 13px var(--font-sans);
	cursor: pointer;
	transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
	&:hover {
		background: var(--surface-sunken-strong);
		color: var(--text-primary);
	}
	/* The 28px chip sits in a 36px row — stretch the touch target on coarse
	   pointers without changing the visual height. */
	@media (pointer: coarse) {
		position: relative;
		&::after {
			content: '';
			position: absolute;
			inset: -4px 0;
		}
	}
	.mirror {
		display: flex;
		html[dir='rtl'] & {
			transform: scaleX(-1);
		}
	}
`

/* ===== Desktop bar (≥1024px) — 64px ===== */
export const DesktopBar = styled.div`
	display: none;
	@media (min-width: 1024px) {
		display: flex;
		align-items: center;
		gap: var(--space-6);
		height: 64px;
		padding: 0 var(--space-6);
	}
`
export const DesktopLogoWrap = styled.div`
	flex: none;
	display: flex;
	align-items: center;
	img {
		display: block;
		height: 32px;
		max-width: 160px;
		object-fit: contain;
	}
`

/* Centered command/search pill (desktop). */
export const HeaderCenter = styled.div`
	display: flex;
	flex: 1;
	justify-content: center;
	padding: 0 var(--space-4);
`
export const SearchPill = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	height: 38px;
	padding: 0 14px;
	width: 360px;
	max-width: 100%;
	border: 1px solid var(--border);
	border-radius: var(--r-md);
	background: var(--surface-sunken);
	color: var(--text-secondary);
	cursor: pointer;
	font: var(--fw-regular) 14px var(--font-sans);
	transition: border-color var(--dur-fast) var(--ease);
	&:hover {
		border-color: var(--blue-20);
	}
	span.placeholder {
		flex: 1;
		text-align: start;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	kbd {
		font: var(--fw-medium) 11px var(--font-mono);
		color: var(--text-disabled);
		border: 1px solid var(--border);
		border-radius: 6px;
		padding: 2px 6px;
		background: var(--surface-card);
	}
`

/* Bordered language toggle (🌐 English). */
export const LangPillButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 36px;
	padding: 0 12px;
	border: 1px solid var(--border);
	border-radius: var(--r-md);
	background: var(--surface-card);
	color: var(--text-secondary);
	font: var(--fw-medium) 14px var(--font-sans);
	cursor: pointer;
	transition: border-color var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
	&:hover {
		border-color: var(--blue-20);
		color: var(--blue);
	}
`
export const DesktopRHS = styled.div`
	display: flex;
	flex: none;
	align-items: center;
	justify-content: flex-end;
`
export const UserPrefsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
`

